import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "./ui/accordion";
import { Reveal } from "./Reveal";
import { Seo } from "./Seo";
import { JsonLd } from "./JsonLd";
import { BOOKING_URL } from "../data/site";
import { track } from "../lib/analytics";

export const ContentPage = ({ data, path, eyebrow }) => {
  const faqSchema = data.faqs && data.faqs.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: data.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  const seg = path.split("/").filter(Boolean);
  const SECTION_NAMES = { industries: "Industries", resources: "Resources" };
  const sectionName = SECTION_NAMES[seg[0]] || seg[0];
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://zentryxsystems.com/" },
      { "@type": "ListItem", position: 2, name: sectionName, item: `https://zentryxsystems.com/${seg[0]}` },
      { "@type": "ListItem", position: 3, name: data.h1, item: `https://zentryxsystems.com${path}` },
    ],
  };

  return (
    <div data-testid="content-page">
      <Seo title={data.metaTitle} description={data.metaDescription} path={path} />
      {faqSchema && <JsonLd data={faqSchema} id={`faq-${path}`} />}
      <JsonLd data={breadcrumb} id={`bc-${path}`} />

      {/* HERO */}
      <section className="pt-32 sm:pt-40 pb-14 px-5 sm:px-8 bg-mist">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            {eyebrow && <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">{eyebrow}</p>}
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-charcoal leading-tight">
              {data.h1}
            </h1>
            {data.whoFor && (
              <p className="mt-6 text-lg text-navy font-medium leading-relaxed border-l-4 border-emerald pl-4">
                {data.whoFor}
              </p>
            )}
          </Reveal>
        </div>
      </section>

      {/* BODY */}
      <section className="py-16 sm:py-20 px-5 sm:px-8">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            {data.intro.map((p) => (
              <p key={p} className="text-lg text-slate-600 leading-relaxed mb-5">{p}</p>
            ))}
          </Reveal>

          {data.video && (
            <Reveal>
              <figure className="mt-6 mb-2">
                <p className="text-slate-600 leading-relaxed mb-4">{data.video.intro}</p>
                <div className="relative w-full overflow-hidden rounded-2xl border border-slate-200 shadow-sm bg-black" style={{ paddingBottom: "56.25%" }}>
                  <iframe
                    className="absolute inset-0 h-full w-full"
                    src={`https://www.youtube.com/embed/${data.video.id}`}
                    title={data.video.title}
                    loading="lazy"
                    data-testid="youtube-embed"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
                <figcaption className="mt-3 text-sm text-slate-500">{data.video.title}</figcaption>
              </figure>
              <JsonLd id={`video-${path}`} data={{
                "@context": "https://schema.org",
                "@type": "VideoObject",
                name: data.video.title,
                description: data.video.description,
                thumbnailUrl: [`https://img.youtube.com/vi/${data.video.id}/maxresdefault.jpg`],
                uploadDate: data.video.uploadDate,
                embedUrl: `https://www.youtube.com/embed/${data.video.id}`,
                contentUrl: `https://www.youtube.com/watch?v=${data.video.id}`,
              }} />
            </Reveal>
          )}

          <div className="mt-10 space-y-8">
            {data.sections.map((s, i) => (
              <Reveal key={s.h} delay={i * 0.05}>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-charcoal mb-3">{s.h}</h2>
                  <p className="text-slate-600 leading-relaxed">{s.p}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* FAQ */}
          {faqSchema && (
            <Reveal>
              <div className="mt-14">
                <h2 className="font-heading text-2xl font-bold text-charcoal mb-6">Frequently asked questions</h2>
                <Accordion type="single" collapsible className="space-y-4">
                  {data.faqs.map((f, i) => (
                    <AccordionItem key={f.q} value={`faq-${i}`} data-testid={`faq-item-${i}`}
                      className="rounded-2xl border border-slate-200 bg-white px-6">
                      <AccordionTrigger className="font-heading font-semibold text-charcoal text-left hover:no-underline py-5">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600 leading-relaxed pb-5">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </Reveal>
          )}

          {/* RELATED */}
          {data.related && data.related.length > 0 && (
            <Reveal>
              <div className="mt-14 rounded-3xl bg-mist border border-slate-200 p-8">
                <h3 className="font-heading text-lg font-bold text-charcoal mb-4">Related</h3>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {data.related.map((r) => (
                    <li key={r.to}>
                      <Link to={r.to} className="inline-flex items-center gap-2 text-navy font-medium hover:gap-3 transition-all">
                        <ArrowRight className="h-4 w-4 flex-shrink-0" /> {r.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 sm:pb-32 px-5 sm:px-8">
        <div className="max-w-4xl mx-auto rounded-3xl bg-navy relative overflow-hidden px-8 sm:px-12 py-14 text-center">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-teal/20 blur-3xl" />
          <Reveal className="relative">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight">
              Let's talk about your firm's technology.
            </h2>
            <div className="mt-7 flex flex-col sm:flex-row gap-4 justify-center">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
                onClick={() => track("book_intro_call_click", { location: path })}
                data-testid="content-booking-cta"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-emerald hover:bg-emerald-dark text-white font-semibold transition-colors">
                Book an Intro Call <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/contact"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold border border-white/20 transition-colors">
                Request a Free IT Assessment
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};
